'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var filterX = _interopDefault(require('ml-array-xy-filter-x'));
var equallySpaced = _interopDefault(require('ml-array-xy-equally-spaced'));
var Stat = _interopDefault(require('ml-stat/array'));
var mlSpectraProcessing = require('ml-spectra-processing');
var jcampconverter = require('jcampconverter');
var mean = _interopDefault(require('ml-array-mean'));

/**
 *
 * @param {Spectrum} spectrum
 * @param {object} [filter={}]
 * @param {array} [filter.from]
 * @param {array} [filter.to]
 * @param {array} [filter.exclusions=[]]
 */

function getData(spectrum, filter = {}) {
  let data = { x: spectrum.x, y: spectrum.y };

  if (filter) {
    data = filterX({ x: spectrum.x, y: spectrum.y }, filter);
  }
  return data;
}

function getNormalized(spectrum, options = {}) {
  if (!Array.isArray(spectrum.x) || !Array.isArray(spectrum.y)) {
    throw new Error('Can not get normalized data');
  }

  let {
    from = spectrum.x[0],
    to = spectrum.x[spectrum.x.length - 1],
    numberOfPoints = 1024,
    filters = [],
    exclusions = []
  } = options;

  let y = spectrum.y.slice(0);

  for (let filter of filters) {
    switch (filter.name) {
      case 'centerMean': {
        let mean = Stat.mean(spectrum.y);
        let meanFct = (y) => y - mean;
        y = y.map(meanFct);
        break;
      }
      case 'scaleSD': {
        let std = Stat.standardDeviation(spectrum.y);
        let stdFct = (y) => y / std;
        y = y.map(stdFct);
        break;
      }
      default:
        throw new Error(`Unknown process kind: ${process.kind}`);
    }
  }

  let result = equallySpaced(
    { x: spectrum.x, y },
    { from, to, numberOfPoints, exclusions }
  );

  return result;
}

function updateRangesInfo(spectrum, ranges = []) {
  spectrum.ranges = {};
  for (let range of ranges) {
    range = JSON.parse(JSON.stringify(range));
    spectrum.ranges[range.label] = range;
    let fromToIndex = {
      fromIndex: mlSpectraProcessing.X.findClosestIndex(spectrum.normalized.x, range.from),
      toIndex: mlSpectraProcessing.X.findClosestIndex(spectrum.normalized.x, range.to)
    };
    range.integration = mlSpectraProcessing.XY.integration(spectrum.normalized, fromToIndex);
    range.maxPoint = mlSpectraProcessing.XY.maxYPoint(spectrum.normalized, fromToIndex);
  }
}

/**
 * Class allowing manipulate one IR spectrum
 * @class spectrum
 * @param {object} [json={}] - object containing a spectrum
 * @param {Array} [json.x=[]] - x values
 * @param {Array} [json.y=[]] - y values
 */
class Spectrum {
  /**
   *
   * @param {array} x
   * @param {array} y
   * @param {string} id
   * @param {object} [options={}]
   */
  constructor(x, y, id, options = {}) {
    const { meta = {}, keepOriginalData = false, normalization = {} } = options;
    if (x && x.length > 1 && x[0] > x[1]) {
      x = x.reverse();
      y = y.reverse();
    } else {
      x = x || [];
      y = y || [];
    }
    if (keepOriginalData) {
      this.x = x;
      this.y = y;
    }
    this.id = id;
    this.meta = meta;
    this.normalized = getNormalized({ x, y }, normalization);
  }

  getXY() {
    if (!Array.isArray(this.x) || !Array.isArray(this.y)) {
      throw new Error('Can not get normalized data');
    }
    return { x: this.x, y: this.y };
  }
}

Spectrum.prototype.getData = function (options) {
  return getData(this, options);
};

Spectrum.prototype.updateNormalization = function (normalization) {
  this.normalized = getNormalized(this, normalization);
  this.ranges = {};
};

Spectrum.prototype.updateRangesInfo = function (ranges) {
  updateRangesInfo(this, ranges);
};

function getJcampKind(data) {
  let datatype = data.spectra[0].dataType.toLowerCase();
  let yUnit = data.spectra[0].yUnit.toLowerCase();

  if (datatype.match(/infrared/)) {
    if (yUnit.match(/absorbance/)) {
      return IR_ABSORBANCE;
    } else {
      return IR_TRANSMITTANCE;
    }
  }
  return undefined;
}

const IR_TRANSMITTANCE = {
  normalization: {},
  importation: {
    converter: (transmittance) => -Math.log10(transmittance)
  },
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

const IR_ABSORBANCE = {
  normalization: {},
  kind: 'Infrared',
  display: {
    xLabel: 'wavelength [cm-1]',
    xInverted: true,
    yLabel: 'Absorbance'
  }
};

/**
 * Creates a new Chromatogram element based in a JCAMP string
 * @param {string} jcamp - String containing the JCAMP data
 * @return {Spectrum} - New class element with the given data
 */
function jcamp(jcamp) {
  const parsed = jcampconverter.convert(jcamp, { xy: true });
  const kind = getJcampKind(parsed);
  const data = parsed.spectra[0].data[0];
  // we convert the data
  if (kind && kind.importation && kind.importation.converter) {
    data.y = data.y.map(kind.importation.converter);
  }

  return { data, kind };
}

function getNormalizationAnnotations(filter = {}) {
  let { exclusions = [] } = filter;
  let annotations = [];
  exclusions = exclusions.filter((exclusion) => !exclusion.ignore);
  annotations = exclusions.map((exclusion) => {
    var annotation = {
      type: 'rect',
      position: [{ x: exclusion.from, y: -200 }, { x: exclusion.to, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    };
    return annotation;
  });
  if (filter.from !== undefined) {
    annotations.push({
      type: 'rect',
      position: [{ x: 0, y: -200 }, { x: filter.from, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    });
  }
  if (filter.to !== undefined) {
    annotations.push({
      type: 'rect',
      position: [{ x: filter.to, y: -200 }, { x: 10000, y: 200 }],
      strokeWidth: 0,
      fillColor: 'rgba(255,255,224,1)'
    });
  }
  return annotations;
}

function addChartDataStyle(data, spectrum) {
  data.styles = {
    unselected: {
      lineColor: spectrum.meta.color || 'darkgrey',
      lineWidth: 1,
      lineStyle: 1
    },
    selected: {
      lineColor: spectrum.meta.color || 'darkgrey',
      lineWidth: 3,
      lineStyle: 1
    }
  };
  data.label = spectrum.meta.id || spectrum.id;
}

/**
 * Retrieve a chart with selected original data
 * @param {*} options
 */
function getChart(spectra, options = {}) {
  const { ids, filter = {} } = options;
  let chart = {
    data: []
  };
  for (let spectrum of spectra) {
    if (!ids || ids.includes(spectrum.id)) {
      let data = spectrum.getData({ filter });
      addChartDataStyle(data, spectrum);
      chart.data.push(data);
    }
  }
  return chart;
}

function getNormalizedChart(spectra, options = {}) {
  const { ids } = options;
  let chart = {
    data: []
  };
  for (let spectrum of spectra) {
    if (!ids || ids.includes(spectrum.id)) {
      let data = spectrum.normalized;
      addChartDataStyle(data, spectrum);
      chart.data.push(data);
    }
  }
  return chart;
}

/**
 * Scaling all the charts so that they match at a specific point
 * @param {array} spectra
 * @param {number} from
 * @param {number} to
 * @param {object} [options={}]
 * @param {array} [options.ids=[]] List of selected spectra to consider, by default all
 * @param {string} [options.method='max'] min, max, range
 *
 */
function getScaledChart(spectra, from, to, options = {}) {
  if (!Array.isArray(spectra) || spectra.length < 1) {
    throw new Error('getScaledChart: No spectra');
  }
  if (from === undefined) {
    throw new Error('getScaledChart: `from` as to be defined');
  }
  if (to === undefined) to = from;

  let fromToIndex = {
    fromIndex: mlSpectraProcessing.X.findClosestIndex(spectra[0].normalized.x, from),
    toIndex: mlSpectraProcessing.X.findClosestIndex(spectra[0].normalized.x, to)
  };

  const { ids, method = 'max' } = options;

  let methodFct;

  switch (method) {
    case 'min':
      methodFct = (spectrum) => mlSpectraProcessing.XY.minYPoint(spectrum, fromToIndex).y;
      break;
    case 'max':
      methodFct = (spectrum) => mlSpectraProcessing.XY.maxYPoint(spectrum, fromToIndex).y;
      break;
    case 'range':
      methodFct = (spectrum) => mlSpectraProcessing.XY.integration(spectrum, fromToIndex);
      break;
    default:
      throw new Error(`getScaledChart: unknown method: ${method}`);
  }

  const length = spectra[0].normalized.x.length;

  let chart = {
    data: []
  };

  let values = spectra.map((spectrum) => methodFct(spectrum.normalized));
  let meanValue = mean(values);

  for (let i = 0; i < spectra.length; i++) {
    let spectrum = spectra[i];

    if (!ids || ids.includes(spectrum.id)) {
      let factor = meanValue / values[i];
      let scaledY = new Array(length);
      for (let j = 0; j < length; j++) {
        scaledY[j] = spectrum.normalized.y[j] * factor;
      }
      let data = {
        x: spectrum.normalized.x,
        y: scaledY
      };
      addChartDataStyle(data, spectrum);
      chart.data.push(data);
    }
  }
  return chart;
}

function getRelativeChart(spectra, targetSpectrum, options = {}) {
  let targetDataY = targetSpectrum.normalized.y;
  const { ids } = options;
  let chart = {
    data: []
  };
  for (let spectrum of spectra) {
    if (!ids || ids.includes(spectrum.id)) {
      let relativeY = new Array(targetDataY.length);
      for (let i = 0; i < targetDataY.length; i++) {
        relativeY[i] = spectrum.normalized.y[i] - targetDataY[i];
      }
      let data = {
        x: spectrum.normalized.x,
        y: relativeY
      };
      addChartDataStyle(data, spectrum);
      chart.data.push(data);
    }
  }
  return chart;
}

class SpectraProcessor {
  /**
   * Manager a large number of spectra with the possibility to normalize the data
   * and skip the original data.
   * It is important to set correctly the options directly because changes
   * will not be possible if the keepOriginalData is not true
   * @param {object} [options={}]
   * @param {boolean} [options.keepOriginalData=False]
   * @param {object} [options.normalization={}] options to normalize the spectra before comparison
   * @param {number} [options.normalization.from]
   * @param {number} [options.normalization.to]
   * @param {number} [options.normalization.numberOfPoints]
   * @param {array<object>} [options.normalization.filters]
   * @param {string} [options.normalization.filters.X.name]
   * @param {object} [options.normalization.filters.X.options]
   * @param {object} [options.rescale={}] rescale spectra based on various parameters
   * @param {string} [options.rescale.range=]
   * @param {string} [options.rescale.method]
   * @param {object} [options.relative={}] display spectra relative to a targe
   * @param {string} [options.relative.target]
   */
  constructor(options = {}) {
    this.options = options;
    this.spectra = [];
  }

  getNormalizationAnnotations() {
    return getNormalizationAnnotations(this.options.normalization);
  }

  /**
   *
   */
  setRescale(rescale) {
    this.options.rescale = rescale;
  }

  setNormalization(normalization = {}) {
    checkOriginal(this, 'Can not change normalization filter.');
    this.options.normalization = normalization;
    for (let spectrum of this.spectra) {
      spectrum.normalization = this.options.normalization;
      spectrum.updateNormalized();
    }
  }

  /**
   * Add jcamp
   * @param {string} jcamp
   * @param {object} [options={}]
   * @param {object} [options.meta={}]
   * @param {string} [options.meta.color]
   * @param {object} [options.id={}]
   * @param {boolean} [options.force=false] replace existing spectrum (same ID)
   */

  addFromJcamp(jcamp$1, options = {}) {
    if (options.force !== true && options.id && this.contains(options.id)) {
      return;
    }

    let parsed = jcamp(jcamp$1);
    this.addFromData(parsed.data, options);
  }

  updateRangesInfo(options) {
    for (let spectrum of this.spectra) {
      spectrum.updateRangesInfo(options);
    }
  }

  /**
   *
   * @param {object} parsed
   * @param {object} [options={}]
   * @param {object} [options.meta={}]
   * @return {Spectrum}
   */

  addFromData(data, options = {}) {
    const id = options.id || Math.random(0).toString(36).substring;
    let index = this.getSpectrumIndex(id);
    if (index === undefined) index = this.spectra.length;
    let spectrum = new Spectrum(data.x, data.y, id, {
      meta: options.meta,
      keepOriginalData: this.options.keepOriginalData,
      normalization: this.options.normalization
    });
    this.spectra[index] = spectrum;
  }

  removeSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.spectra.splice(index, 1);
  }

  removeSpectraNotIn(ids) {
    let currentIDs = this.spectra.map((spectrum) => spectrum.id);
    for (let id of currentIDs) {
      if (!ids.includes(id)) {
        this.removeSpectrum(id);
      }
    }
  }

  contains(id) {
    return !isNaN(this.getSpectrumIndex(id));
  }

  getSpectrumIndex(id) {
    if (!id) return undefined;
    for (let i = 0; i < this.spectra.length; i++) {
      let spectrum = this.spectra[i];
      if (spectrum.id === id) return i;
    }
    return undefined;
  }

  getSpectrum(id) {
    let index = this.getSpectrumIndex(id);
    if (index === undefined) return undefined;
    return this.spectra[index];
  }

  getNormalizedData() {
    if (!this.spectra || !this.spectra[0]) return {};
    let matrix = [];
    let meta = [];
    let ids = [];
    for (let spectrum of this.spectra) {
      ids.push(spectrum.id);
      matrix.push(spectrum.normalized.y);
      meta.push(spectrum.meta);
    }
    let x = this.spectra[0].normalized.x;
    return { ids, matrix, meta, x };
  }

  getChart() {
    checkOriginal(this, 'Can not getChart but you may try getNormalizedChart.');
    return getChart(this.spectra);
  }

  getNormalizedChart(options) {
    return getNormalizedChart(this.spectra, options);
  }

  getScaledChart(from, to, options) {
    return getScaledChart(this.spectra, from, to, options);
  }

  getRelativeChart(targetID, options) {
    let targetSpectrum = this.getSpectrum(targetID);
    if (!targetSpectrum) {
      throw new Error('Target spectrum not found');
    }
    return getRelativeChart(this.spectra, targetSpectrum, options);
  }
}

function checkOriginal(spectraProcessor, message) {
  if (
    !spectraProcessor.options.keepOriginalData &&
    spectraProcessor.spectra.length > 0
  ) {
    throw new Error(
      `${message} Missing original data. Use the option keepOriginalData=true.`
    );
  }
}

exports.SpectraProcessor = SpectraProcessor;
